import React from 'react';
import classNames from 'classnames';
import useStyles from 'src/styles/di-theme';
import useDetectionStyles from './styles/detection';
import {
	Grid,
	Box,
	Typography,
	IconButton,
} from '@barracuda-internal/bds-core-v2';
import { SecurityReport } from 'src/components/SecurityReport';
import moment from 'moment';
import FileCopyIcon from '@mui/icons-material/FileCopy';

interface DetectionDetailSectionsProps {
	esFeed: any;
	feed: any;
	setSearch: (search: string) => void;
	handleCopyClick: () => void;
	onViewScanLog: () => void;
	showInternalUsers: boolean;
	setShowInternalUsers: (show: boolean) => void;
	showExternalUsers: boolean;
	setShowExternalUsers: (show: boolean) => void;
	tagsWithReport: any[];
}

const DetectionDetailSections: React.FC<DetectionDetailSectionsProps> = ({
	esFeed,
	feed,
	setSearch,
	handleCopyClick,
	onViewScanLog,
	showInternalUsers,
	setShowInternalUsers,
	showExternalUsers,
	setShowExternalUsers,
	tagsWithReport,
}) => {
	const classes = useStyles();
	const customClasses = useDetectionStyles();

	const ListItem: React.FC<{ name: string; value: React.ReactNode }> = ({
		name,
		value,
	}) => (
		<div className={classNames(classes['pt_2'])}>
			<Grid
				container
				spacing={2}
				justifyContent="center"
				alignItems="baseline"
			>
				<Grid item xs={6}>
					<Typography
						variant="subtitle2"
						align="right"
						color="textSecondary"
						className={customClasses['font-weight-600']}
					>
						{name}
					</Typography>
				</Grid>
				<Grid item xs={6}>
					<Typography
						component="div"
						align="left"
						variant="body1"
						color="textSecondary"
						className={classes['break_all']}
					>
						{value}
					</Typography>
				</Grid>
			</Grid>
		</div>
	);

	const renderSharePointSite = () => {
		if (feed.cloudType === 'SharepointLibraryConnector') {
			return (
				<ListItem name="SharePoint Site" value={feed.itemLocation} />
			);
		}
	};

	const renderUserAccess = (
		users: string[],
		isExpanded: boolean,
		setExpanded: (expanded: boolean) => void,
	) => {
		if (users.length === 0) {
			return '0 Users';
		}

		if (isExpanded) {
			return (
				<Box
					display="flex"
					alignItems="baseline"
					flexDirection="column"
					flexWrap="wrap"
				>
					<Typography color="secondary" component="span">
						{users.join(', ')}
					</Typography>
					<Typography
						color="primary"
						component="span"
						className={classNames(classes['cursor_pointer'])}
						onClick={() => setExpanded(false)}
					>
						Hide
					</Typography>
				</Box>
			);
		}

		return (
			<Typography
				color="primary"
				className={classes['cursor_pointer']}
				onClick={() => setExpanded(true)}
			>
				{users.length + ' Users'}
			</Typography>
		);
	};

	return (
		<>
			<section className={classNames(classes['ptb_2'])}>
				<Typography variant="h4" color="textPrimary">
					Remediation
				</Typography>
				<Typography
					variant="body2"
					color="textSecondary"
					sx={{ fontSize: '12px' }}
				>
					Update file sharing settings or ask the owner to do so
				</Typography>
				<ListItem
					name="Owner"
					value={
						<Box>
							{esFeed.ownerNames.map((owner, index) => (
								<Typography
									key={index}
									color="primary"
									className={classes['cursor_pointer']}
									onClick={() =>
										setSearch('owner : ' + owner)
									}
								>
									{owner}
								</Typography>
							))}
						</Box>
					}
				/>
				<ListItem
					name="File path"
					value={
						esFeed.filePath.length === 0 ? (
							feed.itemPath
						) : (
							<Box
								className={classNames(
									classes['flex_ver_center'],
									classes['w_full'],
								)}
							>
								<Typography
									color="primary"
									className={classes['cursor_pointer']}
									style={{ width: '80%' }}
									onClick={() =>
										setSearch(
											'filePath : ' +
												esFeed.filePath.join('/'),
										)
									}
								>
									{feed.itemPath}
								</Typography>
								<IconButton
									style={{ padding: '5px' }}
									onClick={handleCopyClick}
								>
									<FileCopyIcon
										className={customClasses['copy-icon']}
									/>
								</IconButton>
							</Box>
						)
					}
				/>
				<ListItem
					name="File History"
					value={
						<Typography
							color="primary"
							className={classes['cursor_pointer']}
							onClick={() => onViewScanLog()}
						>
							View Scan Log
						</Typography>
					}
				/>
				<ListItem name="Source" value={esFeed.cloudType} />

				{renderSharePointSite()}

				<ListItem name="Domain" value={feed.user.name} />
				<ListItem
					name="Report"
					value={
						<SecurityReport
							feedId={esFeed.feedId}
							tagsWithReport={tagsWithReport}
							detections={true}
						/>
					}
				/>
			</section>
			<section className={classNames(classes['ptb_2'])}>
				<Typography variant="h4" color="textPrimary">
					Access
				</Typography>
				<Typography
					variant="body2"
					color="textSecondary"
					sx={{ fontSize: '12px' }}
				>
					How widespread this event is
				</Typography>
				<ListItem
					name="Internal access"
					value={renderUserAccess(
						feed.permissionUsers.internalUsers,
						showInternalUsers,
						setShowInternalUsers,
					)}
				/>
				<ListItem
					name="External access"
					value={renderUserAccess(
						feed.permissionUsers.externalUsers,
						showExternalUsers,
						setShowExternalUsers,
					)}
				/>
				<ListItem
					name="Sharing"
					value={esFeed.sharingLevels.join(', ')}
				/>
			</section>
			<section className={classNames(classes['ptb_2'])}>
				<Typography variant="h4" color="textPrimary">
					Details
				</Typography>
				<Typography
					variant="body2"
					color="textSecondary"
					sx={{ fontSize: '12px' }}
				>
					Extended file information
				</Typography>
				<ListItem name="File type" value={feed.mimeType} />
				<ListItem name="Size" value={feed.size + 'B'} />
				<ListItem
					name="Last detected"
					value={moment(esFeed.lastModified).format('LLL')}
				/>
				<ListItem name="Last Modified User" value={feed.user.name} />
				<ListItem
					name="Last modified"
					value={moment(feed.lastModified).format('LLL')}
				/>
				<ListItem
					name="Created"
					value={moment(feed.created).format('LLL')}
				/>
			</section>
		</>
	);
};

export default DetectionDetailSections;
